/** Transformacions de la barra de format del xat: treballen sobre el text i la selecció. */

export interface EstatText {
  valor: string;
  inici: number;
  fi: number;
}

export type MarcaInline = '**' | '*' | '~~' | '`';
export type TipusLlista = 'pics' | 'numerada';

/**
 * Embolcalla la selecció amb la marca (`**`, `*`, `~~`, `` ` ``). Sense selecció hi insereix les
 * dues marques amb el cursor al mig. Si la selecció ja està embolcallada, la treu.
 */
export function embolcalla({ valor, inici, fi }: EstatText, marca: MarcaInline): EstatText {
  const n = marca.length;
  const abans = valor.slice(Math.max(0, inici - n), inici);
  const despres = valor.slice(fi, fi + n);
  // En cursiva, `**x**` no compta com a `*x*` ja embolcallat.
  const negretaVeina = marca === '*' && (valor[inici - 2] === '*' || valor[fi + 1] === '*');
  if (inici !== fi && abans === marca && despres === marca && !negretaVeina) {
    return {
      valor: valor.slice(0, inici - n) + valor.slice(inici, fi) + valor.slice(fi + n),
      inici: inici - n,
      fi: fi - n,
    };
  }
  return {
    valor: valor.slice(0, inici) + marca + valor.slice(inici, fi) + marca + valor.slice(fi),
    inici: inici + n,
    fi: fi + n,
  };
}

const RE_PREFIX: Record<TipusLlista, RegExp> = { pics: /^- /, numerada: /^\d+\. / };

/**
 * Converteix en llista les línies tocades per la selecció (o la línia del cursor) afegint-hi
 * `- ` o `1. `, `2. `… Si totes ja ho són, treu el prefix.
 */
export function prefixaLinies({ valor, inici, fi }: EstatText, tipus: TipusLlista): EstatText {
  const iniciLinia = valor.lastIndexOf('\n', inici - 1) + 1;
  const fiFinsA = fi > inici && valor[fi - 1] === '\n' ? fi - 1 : fi;
  const salt = valor.indexOf('\n', fiFinsA);
  const fiLinia = salt === -1 ? valor.length : salt;
  const linies = valor.slice(iniciLinia, fiLinia).split('\n');
  const re = RE_PREFIX[tipus];
  const treu = linies.every((l) => re.test(l));
  const noves = linies.map((l, i) => {
    if (treu) return l.replace(re, '');
    const net = l.replace(RE_PREFIX.pics, '').replace(RE_PREFIX.numerada, '');
    return `${tipus === 'pics' ? '- ' : `${i + 1}. `}${net}`;
  });
  const bloc = noves.join('\n');
  const nouValor = valor.slice(0, iniciLinia) + bloc + valor.slice(fiLinia);
  if (inici === fi) {
    // Sense selecció, el cursor es manté al mateix punt del text de la línia.
    const caret = Math.max(
      iniciLinia,
      inici + ((noves[0]?.length ?? 0) - (linies[0]?.length ?? 0)),
    );
    return { valor: nouValor, inici: caret, fi: caret };
  }
  return { valor: nouValor, inici: iniciLinia, fi: iniciLinia + bloc.length };
}
